import React, { useState, useCallback } from "react";
import { Filter, Search, Trash, MapPin, Users, Calendar } from "lucide-react";
import { useInView } from "react-intersection-observer";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface Opportunity {
  id: string;
  name: string;
  community_need?: string;
  volunteer_support?: string;
  impact?: string;
  beneficiary_types?: string[];
  relevant_un_day?: string[];
  focus_area?: string;
  location?: string;
  format?: string;
  outcome?: string;
  image_url?: string;
  meta?: {
    description?: string;
    focus_area?: string;
    beneficiary_type?: string;
    format?: string;
    community_need?: string;
    volunteer_support?: string;
    impact?: string;
    beneficiary_types?: string[];
    relevant_un_day?: string[];
    location?: string;
    outcome?: string;
    image_url?: string;
  };
}

interface OpportunitySelectorProps {
  opportunities: Opportunity[];
  selectedOpportunities: string[];
  isLoadingOpportunities: boolean;
  isFetchingNextPage: boolean;
  hasNextPage?: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onToggleOpportunitySelection: (id: string) => void;
  fetchNextPage: () => void;
}

export function OpportunitySelector({
  opportunities,
  selectedOpportunities,
  isLoadingOpportunities,
  isFetchingNextPage,
  hasNextPage,
  searchQuery,
  onSearchChange,
  onToggleOpportunitySelection,
  fetchNextPage
}: OpportunitySelectorProps) {
  // Track previously loaded items count to highlight new ones
  const [prevItemsCount, setPrevItemsCount] = useState(0);
  const [newItemsIndices, setNewItemsIndices] = useState<number[]>([]);
  
  // Set up intersection observer for infinite scrolling
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1,
    rootMargin: '200px',
    triggerOnce: false,
  });
  
  // Load more when the load more element comes into view
  React.useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, hasNextPage, isFetchingNextPage]);
  
  // Manual trigger for loading more
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  
  // Track newly loaded items
  React.useEffect(() => {
    if (opportunities.length > prevItemsCount) {
      const newIndices = Array.from(
        { length: opportunities.length - prevItemsCount },
        (_, i) => prevItemsCount + i
      );
      setNewItemsIndices(newIndices);
      
      // Clear the highlight after 2 seconds
      const timer = setTimeout(() => {
        setNewItemsIndices([]);
        setPrevItemsCount(opportunities.length);
      }, 2000);
      
      return () => clearTimeout(timer);
    } else if (opportunities.length !== prevItemsCount) {
      // Reset if the count decreased (e.g., due to filtering)
      setPrevItemsCount(opportunities.length);
      setNewItemsIndices([]);
    }
  }, [opportunities.length, prevItemsCount]);

  return (
    <div className="space-y-4">
      <div className="sticky top-0 z-10 bg-white py-2">
        <div className="relative flex-1 shadow-md rounded-lg">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search opportunities..."
            className="pl-9 pr-4 py-6 border-primary/20 focus:border-primary"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>
      
      <div>
        <h3 className="font-medium mb-2">Selected Opportunities: {selectedOpportunities.length}</h3>
        {selectedOpportunities.length > 0 ? (
          <div className="flex flex-wrap gap-2 mb-4">
            {selectedOpportunities.map((id) => {
              const opportunity = opportunities.find(o => o.id === id);
              return (
                <Badge key={id} className="flex items-center gap-1">
                  {opportunity?.name || id}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0"
                    onClick={() => onToggleOpportunitySelection(id)}
                  >
                    <Trash className="h-3 w-3" />
                  </Button>
                </Badge>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground mb-4">No opportunities selected yet</p>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {isLoadingOpportunities && opportunities.length === 0 ? (
          Array.from({ length: 6 }).map((_, index) => (
            <Card key={index} className="overflow-hidden">
              <CardHeader className="p-3">
                <Skeleton className="h-4 w-3/4" />
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <Skeleton className="h-16 w-full mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
          ))
        ) : opportunities.length > 0 ? (
          opportunities.map((opportunity, index) => (
            <Card 
              key={`${opportunity.id}-${index}`} 
              className={cn(
                "overflow-hidden cursor-pointer transition-all h-full",
                selectedOpportunities.includes(opportunity.id) 
                  ? "border-primary" 
                  : "hover:border-primary/50",
                // Highlight newly loaded items
                newItemsIndices.includes(index) && "animate-pulse border-yellow-400",
                "opportunity-card"
              )}
              data-index={index}
              data-new={newItemsIndices.includes(index) ? "true" : "false"}
              onClick={() => onToggleOpportunitySelection(opportunity.id)}
            >
              <div className="relative">
                {(opportunity.image_url || opportunity.meta?.image_url) && (
                  <div className="w-full h-24 overflow-hidden">
                    <img 
                      src={opportunity.image_url || opportunity.meta?.image_url} 
                      alt={opportunity.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Replace broken images with a placeholder
                        (e.target as HTMLImageElement).src = "https://placehold.co/600x400/e2e8f0/64748b?text=No+Image";
                      }}
                    />
                  </div>
                )}
                <div className="absolute top-1 right-1">
                  <Badge variant={selectedOpportunities.includes(opportunity.id) ? "default" : "outline"} className="text-xs px-1.5 py-0">
                    {selectedOpportunities.includes(opportunity.id) ? "Selected" : "Select"}
                  </Badge>
                </div>
              </div>
              <CardHeader className="p-3 pb-1">
                <CardTitle className="text-sm font-semibold line-clamp-1">{opportunity.name}</CardTitle>
                <CardDescription className="text-xs">
                  {(opportunity.focus_area || opportunity.meta?.focus_area || "General").replace(/_/g, " ")}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-3 pt-0 text-xs">
                <p className="line-clamp-2 mb-1">
                  {opportunity.community_need || opportunity.meta?.community_need || "No description available"}
                </p>
                
                {/* Compact additional details */}
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                  {(opportunity.location || opportunity.meta?.location) && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      <span className="line-clamp-1 text-xs">{opportunity.location || opportunity.meta?.location}</span>
                    </div>
                  )}
                  
                  {(opportunity.beneficiary_types?.length > 0 || opportunity.meta?.beneficiary_types?.length > 0) && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Users className="h-3 w-3" />
                      <span className="line-clamp-1 text-xs">
                        {opportunity.beneficiary_types?.join(", ") || opportunity.meta?.beneficiary_types?.join(", ")}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-1 mt-2">
                  {(opportunity.format || opportunity.meta?.format) && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                      {(opportunity.format || opportunity.meta?.format || "").replace(/_/g, " ")}
                    </Badge>
                  )}
                  {(opportunity.volunteer_support || opportunity.meta?.volunteer_support) && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                      {opportunity.volunteer_support || opportunity.meta?.volunteer_support}
                    </Badge>
                  )}
                </div>
              </CardContent>
              <div className={cn(
                "h-1 w-full",
                selectedOpportunities.includes(opportunity.id) ? "bg-primary" : "bg-transparent",
                newItemsIndices.includes(index) && "bg-yellow-400"
              )} />
            </Card>
          ))
        ) : (
          <div className="col-span-full text-center py-8">
            <p className="text-muted-foreground">No opportunities found matching your criteria</p>
          </div>
        )}
      </div>
      
      {/* Infinite scroll loading indicator and load more button */}
      <div 
        className="flex justify-center py-4 mt-2 bg-muted/5 rounded-md"
        id="load-more-container"
        ref={loadMoreRef}
        style={{ minHeight: "60px" }}
      >
        {isFetchingNextPage ? (
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
            <p className="text-sm font-medium">Loading more opportunities...</p>
          </div>
        ) : hasNextPage ? (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadMore}
          >
            Load more opportunities
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">No more opportunities to load</p>
        )}
      </div>
    </div>
  );
}
